import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Search, Loader2, Check, AlertTriangle, RotateCcw, PackageSearch, Truck, ExternalLink } from "lucide-react";
import { cn } from "@/lib/utils";
import { SiteLayout } from "@/components/layout/SiteLayout";
import { PageHero } from "@/components/layout/PageHero";
import { CtaBand } from "@/components/layout/CtaBand";
import { Section } from "@/components/layout/Section";
import { MicroLabel } from "@/components/common/MicroLabel";
import { StratoButton } from "@/components/common/StratoButton";
import { MATERIAL_IMAGES } from "@/assets/materials";
import { STATUS_STATES } from "@/content/pagesContent";
import { normalizeApiError } from "@/api/apiErrors";
import { lookupStatus, toStatusViewRecord } from "@/api/statusApi";
import { ANALYTICS_EVENTS, trackEvent } from "@/api/analyticsApi";

const emailValid = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());

export default function StatoRichiesta() {
  const location = useLocation();
  const [code, setCode] = useState("");
  const [email, setEmail] = useState("");
  // Stati: idle | loading | not_found | error | result
  const [view, setView] = useState("idle");
  const [record, setRecord] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const publicCode = params.get("code");
    if (publicCode) setCode(publicCode);
  }, [location.search]);

  const verify = async (e) => {
    e.preventDefault();

    if (!code.trim() || !emailValid(email)) {
      setErrorMessage("Controlla il codice richiesta e inserisci un'email valida.");
      setView("error");
      return;
    }

    setView("loading");
    setErrorMessage("");
    trackEvent(ANALYTICS_EVENTS.STATUS_LOOKUP_STARTED);

    try {
      const result = await lookupStatus({ publicCode: code, email });
      const viewRecord = toStatusViewRecord(result.request);
      setRecord(viewRecord);
      trackEvent(ANALYTICS_EVENTS.STATUS_LOOKUP_SUCCESS, { publicStage: viewRecord?.publicStage || "" });
      setView("result");
    } catch (error) {
      const safeError = normalizeApiError(error);
      setRecord(null);
      setErrorMessage(safeError.message);
      trackEvent(ANALYTICS_EVENTS.STATUS_LOOKUP_FAILED, { errorCode: safeError.code });
      setView(safeError.code === "not_found" ? "not_found" : "error");
    }
  };

  const reset = () => {
    setCode("");
    setEmail("");
    setRecord(null);
    setErrorMessage("");
    setView("idle");
  };

  const showForm = view === "idle" || view === "loading" || view === "error";

  return (
    <SiteLayout>
      <PageHero
        testId="stato-richiesta-page"
        micro="STATO RICHIESTA"
        title="Controlla lo stato del tuo progetto."
        description="Inserisci il codice richiesta e l'email associata per vedere a che punto è la lavorazione del tuo progetto."
      />

      <Section id="status-lookup-section" tone="offwhite">
        <div className="mx-auto max-w-2xl">
          {/* Lookup form */}
          {showForm ? (
            <form
              onSubmit={verify}
              data-testid="status-lookup-form"
              className="rounded-3xl border border-strato-gray-100 bg-strato-white p-6 shadow-soft sm:p-8"
            >
              <MicroLabel variant="blue">VERIFICA RICHIESTA</MicroLabel>
              <h2 className="mt-3 font-display text-2xl font-medium text-strato-graphite">
                Accedi allo stato
              </h2>
              <div className="mt-6 grid grid-cols-1 gap-4">
                <label className="block">
                  <span className="mb-2 block text-sm font-medium text-strato-graphite">
                    Codice richiesta
                  </span>
                  <input
                    data-testid="status-code"
                    autoComplete="off"
                    spellCheck={false}
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    placeholder="Codice ricevuto via email"
                    className="quote-input font-mono"
                  />
                </label>
                <label className="block">
                  <span className="mb-2 block text-sm font-medium text-strato-graphite">
                    Email associata
                  </span>
                  <input
                    data-testid="status-email"
                    type="email"
                    autoComplete="email"
                    inputMode="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="nome@dominio.it"
                    className="quote-input"
                  />
                </label>
              </div>

              {view === "error" ? (
                <div
                  data-testid="status-error"
                  className="mt-4 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3"
                >
                  <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
                  <span className="text-sm text-amber-700">
                    {errorMessage || "Controlla il codice richiesta e inserisci un'email valida."}
                  </span>
                </div>
              ) : null}

              <StratoButton
                type="submit"
                data-testid="status-verify-button"
                disabled={view === "loading"}
                className="mt-6 w-full"
              >
                {view === "loading" ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Verifica in corso…
                  </>
                ) : (
                  <>
                    <Search className="h-4 w-4" />
                    Verifica richiesta
                  </>
                )}
              </StratoButton>
              <p className="strato-micro mt-4 text-muted-foreground">
                INSERISCI IL CODICE RICHIESTA E L'EMAIL ASSOCIATA
              </p>
            </form>
          ) : null}

          {/* Nessuna richiesta trovata */}
          {view === "not_found" ? (
            <div
              data-testid="status-not-found"
              className="rounded-3xl border border-strato-gray-100 bg-strato-white p-8 text-center shadow-soft sm:p-10"
            >
              <span className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-strato-gray-50">
                <PackageSearch className="h-6 w-6 text-strato-gray-300" />
              </span>
              <h2 className="mt-6 font-display text-xl font-medium text-strato-graphite">
                Nessuna richiesta trovata
              </h2>
              <p className="mx-auto mt-3 max-w-md text-sm text-muted-foreground">
                Non abbiamo trovato una richiesta associata a questi dati. Verifica il codice e
                l'email inseriti oppure contattaci: ti aiutiamo a ritrovare il tuo progetto.
              </p>
              <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
                <StratoButton variant="secondary" data-testid="status-reset" onClick={reset}>
                  <RotateCcw className="h-4 w-4" />
                  Verifica un'altra richiesta
                </StratoButton>
              </div>
            </div>
          ) : null}

          {/* Risultato — reso SOLO con un record reale dal backend.
              In produzione `record` è null, quindi questo blocco non compare
              mai con dati finti. La struttura resta pronta per l'hookup. */}
          {view === "result" && record ? (
            <div data-testid="status-result">
              {/* project summary */}
              <div className="rounded-3xl border border-strato-gray-100 bg-strato-white p-6 shadow-soft sm:p-8">
                <div className="flex flex-col gap-4 border-b border-strato-gray-100 pb-6 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-center gap-3">
                    <MicroLabel className="text-muted-foreground">RIF.</MicroLabel>
                    <span className="font-mono text-sm text-strato-graphite">
                      {record.reference}
                    </span>
                  </div>
                  <span className="inline-flex w-fit items-center gap-2 rounded-full bg-[var(--strato-blue-soft)] px-3 py-1">
                    <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-strato-blue" />
                    <span className="strato-micro text-strato-blue">
                      {STATUS_STATES[record.currentIndex] || STATUS_STATES[0]}
                    </span>
                  </span>
                </div>

                <div className="mt-6 flex items-center gap-4">
                  <div className="h-16 w-16 flex-shrink-0 overflow-hidden rounded-xl bg-strato-gray-50">
                    <img
                      src={MATERIAL_IMAGES[record.color] || MATERIAL_IMAGES["Pure White"] || Object.values(MATERIAL_IMAGES)[0]}
                      alt={record.project}
                      className="h-full w-full object-cover"
                    />
                  </div>
                  <div>
                    <MicroLabel className="text-muted-foreground">PROGETTO</MicroLabel>
                    <p className="font-display text-lg font-medium text-strato-graphite">
                      {record.project}
                    </p>
                  </div>
                </div>

                <dl className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
                  {[
                    { k: "MATERIALE", v: record.material },
                    { k: "COLORE", v: record.color },
                    { k: "QUANTITÀ", v: record.quantity ? `${record.quantity} pz` : "" },
                    { k: "TIPOLOGIA", v: record.type },
                    { k: "DATA RICHIESTA", v: record.requestDate },
                    { k: "ULTIMO AGGIORNAMENTO", v: record.lastUpdate },
                  ].filter((d) => String(d.v || "").trim()).map((d) => (
                    <div
                      key={d.k}
                      className="rounded-xl border border-strato-gray-100 bg-strato-gray-50 px-4 py-3"
                    >
                      <MicroLabel className="text-muted-foreground">{d.k}</MicroLabel>
                      <p className="mt-1 font-display text-sm font-medium text-strato-graphite">
                        {d.v}
                      </p>
                    </div>
                  ))}
                </dl>
              </div>

              {record.fulfillment ? (
                <div
                  data-testid="status-fulfillment"
                  className="mt-6 rounded-3xl border border-strato-gray-100 bg-strato-white p-6 shadow-soft sm:p-8"
                >
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                      <MicroLabel variant="blue">PRODUZIONE E SPEDIZIONE</MicroLabel>
                      <h2 className="mt-3 font-display text-xl font-medium text-strato-graphite">
                        {record.fulfillment.label || "Aggiornamento ordine"}
                      </h2>
                      {record.fulfillment.customerNote ? (
                        <p className="mt-2 text-sm leading-6 text-muted-foreground">
                          {record.fulfillment.customerNote}
                        </p>
                      ) : null}
                    </div>
                    <span className="grid h-12 w-12 flex-shrink-0 place-items-center rounded-2xl bg-[var(--strato-blue-soft)] text-strato-blue">
                      <Truck className="h-5 w-5" />
                    </span>
                  </div>

                  <dl className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
                    {[
                      { k: "CORRIERE", v: record.fulfillment.carrier },
                      { k: "TRACKING", v: record.fulfillment.trackingNumber },
                      { k: "SPEDITO IL", v: record.fulfillment.shippedAt },
                      { k: "CONSEGNATO IL", v: record.fulfillment.deliveredAt },
                    ].filter((d) => String(d.v || "").trim()).map((d) => (
                      <div
                        key={d.k}
                        className="rounded-xl border border-strato-gray-100 bg-strato-gray-50 px-4 py-3"
                      >
                        <MicroLabel className="text-muted-foreground">{d.k}</MicroLabel>
                        <p className="mt-1 font-display text-sm font-medium text-strato-graphite">
                          {d.v}
                        </p>
                      </div>
                    ))}
                  </dl>

                  {record.fulfillment.trackingUrl ? (
                    <a
                      href={record.fulfillment.trackingUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-6 inline-flex items-center gap-2 rounded-full bg-strato-blue px-5 py-3 text-sm font-medium text-white transition hover:bg-strato-blue-hover"
                    >
                      Apri tracking
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  ) : null}
                </div>
              ) : null}

              {/* timeline */}
              <div className="mt-6 rounded-3xl border border-strato-gray-100 bg-strato-white p-6 shadow-soft sm:p-8">
                <MicroLabel variant="blue">TIMELINE STATO</MicroLabel>
                <ol className="mt-6" data-testid="status-timeline">
                  {STATUS_STATES.map((s, i) => {
                    const done = i < record.currentIndex;
                    const current = i === record.currentIndex;
                    return (
                      <li
                        key={s}
                        data-testid={`status-timeline-step-${i}`}
                        className="relative flex items-start gap-4 border-l border-strato-gray-200 pb-6 pl-6 last:border-l-transparent last:pb-0"
                      >
                        <span
                          className={cn(
                            "absolute -left-[13px] top-0 grid h-6 w-6 place-items-center rounded-full border text-[10px] font-semibold",
                            done
                              ? "border-strato-blue bg-strato-blue text-white"
                              : current
                                ? "border-strato-blue bg-[var(--strato-blue-soft)] text-strato-blue"
                                : "border-strato-gray-200 bg-strato-white text-strato-gray-300",
                          )}
                        >
                          {done ? <Check className="h-3 w-3" strokeWidth={3} /> : i + 1}
                        </span>
                        <div className="-mt-0.5">
                          <p
                            className={cn(
                              "font-display text-[15px] font-medium",
                              done || current ? "text-strato-graphite" : "text-strato-gray-300",
                            )}
                          >
                            {s}
                          </p>
                          {current ? (
                            <MicroLabel className="text-strato-blue">IN CORSO</MicroLabel>
                          ) : null}
                        </div>
                      </li>
                    );
                  })}
                </ol>
                <p className="strato-micro mt-6 text-muted-foreground">
                  STATO AGGIORNATO A OGNI FASE DELLA LAVORAZIONE
                </p>
              </div>

              <div className="mt-6 flex justify-center">
                <StratoButton variant="secondary" data-testid="status-reset" onClick={reset}>
                  <RotateCcw className="h-4 w-4" />
                  Verifica un'altra richiesta
                </StratoButton>
              </div>
            </div>
          ) : null}
        </div>
      </Section>

      <CtaBand secondaryTo="/contatti" secondaryLabel="Contattaci" />
    </SiteLayout>
  );
}
